
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader, Plus } from 'lucide-react';

const MAX_MANUAL_PLAN_DAYS = 7;

interface CopyDayModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fromDay: number;
  availableDays: number[];
  onCopyDays: (fromDay: number, toDays: number[]) => Promise<any | void>;
  onAddDateForCopy?: (fromDay: number) => Promise<number | null>;
}

const CopyDayModal: React.FC<CopyDayModalProps> = ({
  open,
  onOpenChange,
  fromDay,
  availableDays,
  onCopyDays,
  onAddDateForCopy,
}) => {
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingDate, setIsAddingDate] = useState(false);
  const hasReachedMaxDays = availableDays.length >= MAX_MANUAL_PLAN_DAYS;

  // Reset selections whenever modal opens or source day changes
  useEffect(() => {
    if (open) {
      setSelectedDays([]);
      setIsAddingDate(false);
    }
  }, [open, fromDay]);

  const handleCopy = async () => {
    if (selectedDays.length > 0) {
      setIsLoading(true);
      try {
        await onCopyDays(fromDay, selectedDays);
        onOpenChange(false);
        setSelectedDays([]);
      } catch (error) {
        console.error('Error copying day:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddDate = async () => {
    if (!onAddDateForCopy || hasReachedMaxDays) return;
    setIsAddingDate(true);
    try {
      const newDay = await onAddDateForCopy(fromDay);
      if (newDay) {
        setIsLoading(true);
        await onCopyDays(fromDay, [newDay]);
        onOpenChange(false);
        setSelectedDays([]);
      }
    } catch (error) {
      console.error('Error adding date for copy:', error);
    } finally {
      setIsLoading(false);
      setIsAddingDate(false);
    }
  };

  const availableTargetDays = availableDays.filter(day => day !== fromDay);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Copy Day {fromDay} Meals</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <p className="text-xs text-gray-700">
              Plan length: <span className="font-semibold">{availableDays.length}/{MAX_MANUAL_PLAN_DAYS}</span> days
            </p>
          </div>

          <p className="text-sm text-gray-600 leading-relaxed">
            Select one or more days to copy <strong>Day {fromDay}'s</strong> meals to. 
            Any existing meals on the destination days will be replaced.
          </p>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-sm font-medium">Copy to:</label>
              {onAddDateForCopy && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddDate}
                  disabled={isLoading || isAddingDate || hasReachedMaxDays}
                >
                  {isAddingDate ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Day
                    </>
                  )}
                </Button>
              )}
            </div>
            {hasReachedMaxDays && (
              <p className="text-xs text-amber-700">
                Maximum reached. Manual meal plans support up to 7 days.
              </p>
            )}
            <div className="grid grid-cols-3 gap-2">
              {availableTargetDays.map(day => (
                <Button
                  key={day}
                  variant={selectedDays.includes(day) ? "default" : "outline"}
                  onClick={() => {
                    setSelectedDays(prev => (
                      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
                    ));
                  }}
                  disabled={isLoading || isAddingDate}
                  className="w-full"
                >
                  Day {day}
                </Button>
              ))}
            </div>
            {availableTargetDays.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-2">
                No other days available to copy to.
              </p>
            )}
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading || isAddingDate}>
            Cancel
          </Button>
          <Button onClick={handleCopy} disabled={selectedDays.length === 0 || isLoading || isAddingDate}>
            {isLoading ? (
              <>
                <Loader className="h-4 w-4 mr-2 animate-spin" />
                Copying...
              </>
            ) : (
              'Copy Meals'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyDayModal;
